export const isServer = typeof window === 'undefined';
// only SSR since it is held inside `process.env`
export const D356_DEFAULT_VERSION = process.env.D365_CRT_VERSION || '7.3';
/**
 * Getting host information based on browser in CSR or request in SSR
 * @see https://docs.edg.io/applications/v7/performance/request#request-headers
 * @see https://en.wikipedia.org/wiki/List_of_HTTP_header_fields
 * Using [fail-fast strategy](https://en.wikipedia.org/wiki/Fail-fast_system) since recovery in bad routing is unlikely.
 *
 */
type Host = { host: string; proto: string };
/**
 * Gives host name as  "local" for serving box if SSR (it might be behind router and firewall)
 */
export const servingHost = async (): Promise<Host> => {
  if (isServer) {
    const res = await import('next/headers').then(async ({ headers }) => {
      const host = (await headers()).get('host');
      const proto = (await headers()).get('X-Forwarded-Proto'); // Default to same port as origin, in future may use `Front-End-Https`
      if (host && proto) return { host, proto };
      throw new Error(
        `Error in proxy configuration, no information host:${host} proto:${proto}`,
      );
    });
    console.debug('servingHost', res);
    return res;
  }
  return frontHost();
};
/**
 * Gives host name as  "original request" for serving box if SSR
 */
export const frontHost = async () => {
  if (isServer) {
    if (
      process.env.NODE_ENV !== 'production' ||
      process.env.DEV_MODE === 'true'
    ) {
      const res = await import('next/headers').then(async ({ headers }) => {
        const host =
          (await headers()).get('x-0-forwarded-host') ??
          (await headers()).get('X-Forwarded-Host') ??
          (await headers()).get('host');
        const proto = (await headers()).get('X-Forwarded-Proto');
        if (host && proto) return { host, proto };
        throw new Error(
          `Error in proxy configuration, no information host:${host} proto:${proto}`,
        );
      });
      console.debug('frontHost', res);
      return res;
    }
    return { host: process.env.CURRENT_DOMAIN || '', proto: 'https' };
  }
  return {
    proto: window.location.protocol.replace(/:$/, ''),
    host: window.location.host,
  };
};

/**
 * Gives absolute URL for "internal API"
 * @param apiName - API name (folders path to route with no leading slash expected)
 */
export async function getApiUrl(apiName: string) {
  const { host, proto } = await servingHost();
  console.debug('hostname', host, proto, isServer ? 'server' : 'client');
  return `${proto}://${host}/cart/api/${apiName.replace(/^\//, '')}`;
}

/**
 * Give absolute url for the page
 * @param path - URL path (with leading slash expected)
 */
export async function getPageUrl(path: string) {
  const { host, proto } = await frontHost();
  console.debug('hostname', host, proto, isServer ? 'server' : 'client');
  return `${proto}://${host}/${path.replace(/^\//, '')}`;
}

/**
 * Construct full URL based on environment variable, could be used only SSR since it is held inside `process.env`
 *  and cross-origin call restrictions.
 * @param {string} relativeUrl
 * @returns {string} full url
 * @throws {TypeError} invalid base or relative part of URL
 */
export function getCrtUrl(relativeUrl: string): URL {
  const crtHost = process.env.D365_CRT_URL;
  if (crtHost && relativeUrl) {
    const fullPath = `${crtHost.replace(/\/$/, '')}/${relativeUrl.replace(/^\//, '')}`;
    return new URL(fullPath);
  }
  throw new TypeError('Invalid Crt url', { cause: { crtHost, relativeUrl } });
}
